import React from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  genElBoolean,
  genElDate,
  genElObject,
  genElText,
} from "../../../../components/utils/EditorDialogElementGenerator";
import KendaraanSelectorDialog from "../../../../dialogs/selectors/KendaraanSelectorDialog";
import { Divider } from "@mui/material";
import KaryawanSelectorDialog from "../../../../dialogs/selectors/KaryawanSelectorDialog";
import ProblemStatusToggle from "./ProblemStatusToggle";

const editTitle = "Edit Problem Kendaraan";
const addTitle = "Tambah Problem Kendaraan";

const ProblemEditorDialog = (props) => {
  const {
    visible,
    onHide,
    onConfirm,
    loading,
    title,
    data,
    setData: setDataOfPage,
    addNewMode,
    error,
    setError,
  } = props;

  // dialog state
  const [showKendaraanSelector, setShowKendaraanSelector] =
    React.useState(false);
  const [showPnjSelector, setShowPnjSelector] = React.useState(false);

  // Everything of this dialog changes the data through here.
  // Tanggal Selesai filled in: the problem is solved (status 3).
  // Tanggal Selesai emptied on a solved problem: it is not fixed after all, back to schedule (status 2).
  const setData = (newData) => {
    if (newData.tanggal_selesai !== data.tanggal_selesai) {
      if (newData.tanggal_selesai) {
        newData = { ...newData, status: 3 };
      } else if (data.status === 3) {
        newData = { ...newData, status: 2 };
      }
    }
    setDataOfPage(newData);
  };

  // Schedule / Ignore button: the problem starts over with that status, so Tanggal Selesai and
  // who / when handled it (Di tangani, Di tangani Tanggal) are emptied. (Tanggal Selesai would also make it solved again.)
  const handleOnChangeStatus = (status) => {
    setDataOfPage({
      ...data,
      status,
      tanggal_selesai: null,
      di_tangani_id: null,
      di_tangani_datetime: null,
      data_di_tangani: null,
    });
  };

  /**
   * Validates the form data.
   *
   * @returns {boolean} - Returns true if the data is valid, otherwise false.
   * @returns {object} - Returns an object containing the error messages, format { field: message}.
   */
  const validate = () => {
    let valid = true;
    let newEror = {};

    const keterangan = data.keterangan
      ? data.keterangan.trim()
      : data.keterangan;
    if (keterangan === "" || keterangan === null || keterangan === undefined) {
      newEror = { ...newEror, keterangan: "Keterangan harus diisi" };
      valid = false;
    }

    // solved (Tanggal Selesai filled in): somebody handled it, so Di tangani and Di tangani Tanggal can't be empty
    if (data.tanggal_selesai) {
      if (!data.di_tangani_id) {
        newEror = {
          ...newEror,
          "data_di_tangani.nama": "Di tangani harus diisi",
        };
        valid = false;
      }
      if (!data.di_tangani_datetime) {
        newEror = {
          ...newEror,
          di_tangani_datetime: "Di tangani Tanggal harus diisi",
        };
        valid = false;
      }
    }

    if (!valid) setError(newEror);

    return valid;
  };

  /**
   * Generate Dialog Form Element
   *
   * various function to generate
   *
   * genElText({ field, name, data, setData, disabled, autoFocus, error })
   * genElTextPassword({ field, name, data, setData, disabled, autoFocus, error })
   * genElTextDropdown({ field, name, data, setData, options, disabled, autoFocus, error, optionLabel = "name" })
   * genElNumber({ field, name, data, setData, min_fraction_digits, max_fraction_digits, min, max, disabled, autoFocus, error })
   * genElNumberDropdown({ field, name, data, setData, options, disabled, autoFocus, error, optionLabel = "name" })
   * genElBoolean({ field, name, data, setData, labelTrue = "Ya", labelFalse = "Tidak", disabled, autoFocus, error })
   * genElDate({ field, name, data, setData, primeFormat, min, max, showTime, time, month, year, disabled, autoFocus, error })
   * genElObject({ field, name, value, onClick, selectorGenerator, disabled, autoFocus, error })
   */

  const handleOnClickKendaraan = () => {
    setShowKendaraanSelector(true);
  };

  const handleOnConfirmKendaraan = (event, datas) => {
    let selected;
    event.preventDefault();
    if (datas && datas.length > 0) {
      selected = datas[0];
      setData({
        ...data,
        data_kendaraan: selected,
        kendaraan_id: selected.id,
      });
      setShowKendaraanSelector(false);
    }
    return selected;
  };

  const handleOnClickPnj = () => {
    setShowPnjSelector(true);
  };

  const handleOnConfirmPnj = (event, datas) => {
    let selected;
    event.preventDefault();
    if (datas && datas.length > 0) {
      selected = datas[0];
      setData({
        ...data,
        data_di_tangani: selected,
        di_tangani_id: selected.id,
      });
      setShowPnjSelector(false);
    }
    return selected;
  };

  const elKendaraan = genElObject({
    field: "data_kendaraan.nomor_polisi",
    name: "Kendaraan",
    value: data.data_kendaraan ? data.data_kendaraan.nomor_polisi : "",
    onClick: handleOnClickKendaraan,
    disabled: true,
  });
  const elKeterangan = genElText({
    field: "keterangan",
    name: "Keterangan",
    data,
    setData,
    error,
    disabled: true,
  });
  const elTanggal = genElDate({
    field: "tanggal",
    name: "Tanggal",
    data,
    setData,
    error,
    showTime: true,
    disabled: true,
  });
  const elDitangani = genElObject({
    field: "data_di_tangani.nama",
    name: "Di tangani",
    value: data.data_di_tangani ? data.data_di_tangani.nama : "",
    onClick: handleOnClickPnj,
    error,
  });
  const elDitanganiTanggal = genElDate({
    field: "di_tangani_datetime",
    name: "Di tangani Tanggal",
    data,
    setData,
    error,
    showTime: true,
  });
  const elTanggalSelesai = genElDate({
    field: "tanggal_selesai",
    name: "Tanggal Selesai",
    data,
    setData,
    error,
    showTime: true,
  });
  const elNote = genElText({
    field: "note",
    name: "Note",
    data,
    setData,
    error,
  });
  const elShowDashboard = genElBoolean({
    field: "show_dashboard",
    name: "Show Dashboard",
    data,
    setData,
    error,
  });

  const dialogFooter = (
    <React.Fragment>
      <Button
        label="Cancel"
        icon="pi pi-times"
        outlined
        onClick={props.onHide}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        onClick={(e) => {
          const valid = validate();
          if (valid) {
            // with a Tanggal Selesai the problem is solved
            onConfirm(data.tanggal_selesai ? { ...data, status: 3 } : data);
          }
        }}
      />
    </React.Fragment>
  );

  return (
    <Dialog
      visible={visible}
      style={{ width: "45rem" }}
      breakpoints={{ "960px": "75vw", "641px": "90vw" }}
      header={addNewMode ? addTitle : editTitle}
      modal
      className={`p-fluid`}
      footer={dialogFooter}
      onHide={onHide}
    >
      <KendaraanSelectorDialog
        title={"Pilih Kendaraan"}
        visible={showKendaraanSelector}
        onHide={() => setShowKendaraanSelector(false)}
        onConfirm={handleOnConfirmKendaraan}
      />

      <KaryawanSelectorDialog
        title="Pilih PNJ"
        visible={showPnjSelector}
        onHide={() => setShowPnjSelector(false)}
        onConfirm={handleOnConfirmPnj}
        additionalFilter={"jabatan_id:=:3"}
      />

      <div className="col-start-center relative h-100 gap-3">
        <ProblemStatusToggle
          status={data.status}
          onChange={handleOnChangeStatus}
        />
        <div
          className={`flex flex-col md:flex-row justify-start items-start w-100 gap-3`}
        >
          <div className="col-start-start w-100 gap-3">
            {elTanggal}
            {elKendaraan}
            {elKeterangan}
            {elNote}
          </div>
          <Divider layout="vertical" className="h-[23.7rem] hidden md:flex" />
          <div className="col-start-start w-full gap-3">
            {elDitangani}
            {elDitanganiTanggal}
            {elTanggalSelesai}
            {elShowDashboard}
          </div>
          <div className="col-start-start"></div>
        </div>
        {loading && (
          <div
            className={`col-center-center w-100  gap-2 absolute z-100 h-100 bg-black/20 rounded `}
          >
            <ProgressSpinner />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default ProblemEditorDialog;
